import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMyAccess } from '../../api/access-control-api';
import { Loading, ErrorMessage } from '../../components/Loading';
import { DynamicFormsPermission, type AccessContext as AccessContextModel } from '../../models/access-control';

interface AccessState {
  access: AccessContextModel;
  hasPermission: (permission: number, formId?: number) => boolean;
  hasFeature: (featureKey: string) => boolean;
}

const denied: AccessContextModel = {
  IsAdministrator: false, IsSuperUser: false, GlobalPermissionMask: 0, FormPermissionMasks: {}, CanAccessDashboard: false,
  CanViewForms: false, CanCreateForms: false, CanEditAnyForm: false, CanViewAnySubmissions: false,
  CanManageAnySubmissions: false, CanViewAnyReports: false, CanManageAnyReports: false,
  CanManageSettings: false, CanManageAccess: false, CanManagePackages: false, CanManagePortalFeatures: false,
  CanManageSmsSettings: false, CanToggleSms: false,
  CanCloneForms: false, CanDeleteForms: false, MaxForms: null, FormCount: 0, EffectiveFeatures: []
};

const Context = createContext<AccessState>({ access: denied, hasPermission: () => false, hasFeature: () => false });

function normalize(mask: number): number {
  if (mask & DynamicFormsPermission.EditForms) mask |= DynamicFormsPermission.ViewForms;
  if (mask & DynamicFormsPermission.ManageSubmissions) mask |= DynamicFormsPermission.ViewSubmissions;
  if (mask & DynamicFormsPermission.ManageReports) mask |= DynamicFormsPermission.ViewReports;
  return mask;
}

export function AccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<AccessContextModel>();
  const [error, setError] = useState<unknown>();
  useEffect(() => { getMyAccess().then(setAccess).catch(setError); }, []);

  const value = useMemo<AccessState>(() => ({
    access: access ?? denied,
    hasPermission: (permission: number, formId = 0) => {
      if (access?.IsAdministrator) return true;
      const global = normalize(access?.GlobalPermissionMask ?? 0);
      if ((global & permission) === permission) return true;
      if (formId <= 0) return false;
      const formMask = normalize(Number(access?.FormPermissionMasks?.[String(formId)] ?? 0));
      return (formMask & permission) === permission;
    },
    hasFeature: (featureKey: string) => Boolean(access?.IsSuperUser || access?.EffectiveFeatures?.includes(featureKey))
  }), [access]);

  if (error) return <ErrorMessage error={error} />;
  if (!access) return <Loading />;
  if (!access.CanAccessDashboard) return <div className="alert alert-danger">اجازه ورود به داشبورد فرم‌ها را ندارید.</div>;
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAccess() { return useContext(Context); }

export function AccessRoute({ permission, formId, children }: { permission: number; formId?: number; children: ReactNode }) {
  const { hasPermission } = useAccess();
  return hasPermission(permission, formId) ? <>{children}</> : <div className="alert alert-danger">اجازه مشاهده این بخش را ندارید.</div>;
}
